// Define threshold structure
export interface ThresholdValues {
  low: number
  medium: number
  better: 'higher' | 'lower'
}

// Metric information interface
export interface MetricInfo {
  title: string
  description: string
}

// Threshold configuration for each metric type
export const metricThresholds: Record<string, ThresholdValues> = {
  mAP: { low: 50, medium: 70, better: 'higher' },
  Precision: { low: 50, medium: 85, better: 'higher' },
  Recall: { low: 50, medium: 85, better: 'higher' },
  'F1 Score': { low: 50, medium: 85, better: 'higher' },
  'Validation Accuracy': { low: 60, medium: 75, better: 'higher' },
  'Train Accuracy': { low: 60, medium: 75, better: 'higher' },
  'Cross Entropy': { low: 1.0, medium: 0.5, better: 'lower' },
  'Smooth L1': { low: 0.5, medium: 0.1, better: 'lower' },
}

// Colors for different performance levels
export const metricColors = {
  low: '#FF4500', // Red for low performance
  medium: '#FFD700', // Yellow for medium performance
  high: '#32CD32', // Green for high performance
}

// Descriptions and information for each metric type
export const metricInfoMap: Record<string, MetricInfo> = {
  mAP: {
    title: 'mAP',
    description:
      'Measures overall detection accuracy by balancing both how precise and how complete the detections are for this model. A higher score means more correct detections with fewer misses.',
  },
  'F1 Score': {
    title: 'F1 Score',
    description:
      'Combines precision and recall into one score. A higher F1 means the model finds most real positives (high recall) without making too many false claims (high precision).',
  },
  Precision: {
    title: 'Precision',
    description:
      'Shows how many of the model\'s "positive" predictions are actually correct. Higher precision means fewer wrong alerts.',
  },
  Recall: {
    title: 'Recall',
    description:
      'Shows how many of the real positives the model successfully detects. Higher recall means fewer missed cases.',
  },
  'Validation Accuracy': {
    title: 'Validation Accuracy',
    description:
      'Indicates how accurately the model classifies items in the validation dataset that it has not seen during training. Higher accuracy means better generalization.',
  },
  'Train Accuracy': {
    title: 'Train Accuracy',
    description:
      'Measures how well the model performs on the training dataset. High train accuracy with low validation accuracy can indicate overfitting.',
  },
  'Cross Entropy': {
    title: 'Cross Entropy',
    description:
      'A loss function that measures the performance of a classification model whose output is a probability value between 0 and 1. Lower values indicate better model performance.',
  },
  'Smooth L1': {
    title: 'Smooth L1',
    description:
      "A loss function that handles outliers better than mean squared error. It's less sensitive to large errors and produces more stable gradients. Lower values indicate better model performance.",
  },
}

// Get info for a specific metric
export const getMetricInfo = (metricName: string): MetricInfo => {
  return (
    metricInfoMap[metricName] || {
      title: metricName,
      description:
        'This metric helps gauge model performance. Higher values typically mean better results.',
    }
  )
}

// Function to determine color based on metric key and value
export const getMetricColor = (metricName: string, value: number): string => {
  const defaultThresholds: ThresholdValues = {
    low: 50,
    medium: 75,
    better: 'higher',
  }
  const thresholds = Object.prototype.hasOwnProperty.call(metricThresholds, metricName)
    ? metricThresholds[metricName]
    : defaultThresholds

  if (thresholds.better === 'higher') {
    if (value < thresholds.low) {
      return metricColors.low
    } else if (value < thresholds.medium) {
      return metricColors.medium
    } else {
      return metricColors.high
    }
  } else {
    // For 'lower is better' metrics, invert the logic
    if (value < thresholds.medium) {
      return metricColors.high
    } else if (value < thresholds.low) {
      return metricColors.medium
    } else {
      return metricColors.low
    }
  }
}
